import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  Business as PortalIcon,
  SupervisorAccount as AdminIcon,
} from "@mui/icons-material";
import { useAnalytics } from "./hooks/useAnalytics";
import { usePortals } from "@/containers/Portals/hooks/usePortals";
import { useSystemAdmins } from "@/containers/Users/hooks/useSystemAdmins";
import { ROUTES } from "@/routes/config";

interface StatCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ReactNode;
  color?: string;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  color = "primary.main",
  onClick,
}) => (
  <Card
    sx={{
      height: "100%",
      cursor: onClick ? "pointer" : "default",
      "&:hover": onClick ? { boxShadow: 4 } : {},
    }}
    onClick={onClick}
  >
    <CardContent>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography color="text.secondary" variant="body2" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={700}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            p: 1,
            borderRadius: 2,
            bgcolor: `${color}15`,
            color: color,
          }}
        >
          {icon}
        </Box>
      </Stack>
    </CardContent>
  </Card>
);

const statusChipColor = (
  status: string,
): "success" | "info" | "warning" | "error" | "default" => {
  switch (status) {
    case "active":
      return "success";
    case "provisioning":
      return "info";
    case "pending":
      return "warning";
    case "suspended":
      return "error";
    default:
      return "default";
  }
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { analytics, isLoading } = useAnalytics();
  const { portals } = usePortals();
  const { admins: systemAdmins } = useSystemAdmins();

  const recentPortals = useMemo(
    () =>
      [...portals]
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        )
        .slice(0, 5),
    [portals],
  );

  const recentUsers = useMemo(
    () =>
      [...systemAdmins]
        .sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        )
        .slice(0, 5),
    [systemAdmins],
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Dashboard
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Platform overview and system analytics
      </Typography>

      <Grid container spacing={3}>
        {/* Stats Cards */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Portals"
            value={analytics?.total_portals ?? 0}
            subtitle={`${analytics?.active_portals ?? 0} active`}
            icon={<PortalIcon />}
            color="primary.main"
            onClick={() => navigate(ROUTES.PORTALS)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="System Admins"
            value={analytics?.total_system_admins ?? 0}
            icon={<AdminIcon />}
            color="secondary.main"
            onClick={() => navigate(ROUTES.USERS)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Portal Admins"
            value={analytics?.total_portal_admins ?? 0}
            icon={<AdminIcon />}
            color="warning.main"
            onClick={() => navigate(`${ROUTES.USERS}?tab=portal`)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2.5, height: "100%" }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1.5 }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Recently Added Portals
              </Typography>
              <Typography
                variant="body2"
                color="primary.main"
                sx={{ cursor: "pointer", fontWeight: 600 }}
                onClick={() => navigate(ROUTES.PORTALS)}
              >
                View all
              </Typography>
            </Stack>
            {recentPortals.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No portals yet.
              </Typography>
            ) : (
              <Stack divider={<Divider flexItem />}>
                {recentPortals.map((portal) => (
                  <Stack
                    key={portal.id}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      py: 1.25,
                      cursor: "pointer",
                      "&:hover": { bgcolor: "action.hover" },
                      px: 1,
                      mx: -1,
                      borderRadius: 1,
                    }}
                    onClick={() =>
                      navigate(`/dashboard/portals/${portal.id}`, {
                        state: { portal },
                      })
                    }
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700} noWrap>
                        {portal.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontFamily:
                            "ui-monospace, SFMono-Regular, Menlo, monospace",
                        }}
                      >
                        {portal.slug}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Chip
                        label={
                          portal.status.charAt(0).toUpperCase() +
                          portal.status.slice(1)
                        }
                        color={statusChipColor(portal.status)}
                        size="small"
                      />
                      <Typography variant="caption" color="text.secondary">
                        {new Date(portal.created_at).toLocaleDateString()}
                      </Typography>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2.5, height: "100%" }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1.5 }}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Recently Added Users
              </Typography>
              <Typography
                variant="body2"
                color="primary.main"
                sx={{ cursor: "pointer", fontWeight: 600 }}
                onClick={() => navigate(ROUTES.USERS)}
              >
                View all
              </Typography>
            </Stack>
            {recentUsers.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No users yet.
              </Typography>
            ) : (
              <Stack divider={<Divider flexItem />}>
                {recentUsers.map((user) => {
                  const name =
                    user.first_name || user.last_name
                      ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
                      : user.email;
                  return (
                    <Stack
                      key={user.id}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ py: 1.25, px: 1, mx: -1 }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>
                          {name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontFamily:
                              "ui-monospace, SFMono-Regular, Menlo, monospace",
                          }}
                        >
                          {user.email}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(user.created_at).toLocaleDateString()}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
